/**
 * F 243 — the encryption UI. F 237 stopped at its Stage 0 gate because the
 * seven F 233 helpers were unreachable from `freshcards/core`; F 238 Item 1
 * (commit 8b96051) shipped the re-exports, and this component is the first
 * consumer that reaches them through the PUBLIC SURFACE.
 *
 * Every import below comes from `../../../core` — the barrel — never from
 * `core/credentials/vault` or `core/anchors`. That is not style. The F 237
 * gate existed precisely because in-repo code could reach the source files
 * while every actual consumer got nothing; a UI that imports the source
 * directly would re-open that exact hole while appearing to work.
 *
 * WHAT THIS COMPONENT IS
 * ----------------------
 *   1. an encryption toggle for the node currently in the detail view
 *   2. an anchor editor — the plaintext hint the AI can search
 *   3. a passphrase prompt for the local credential store
 *   4. a locked/unlocked status line
 *
 * WHAT IT IS NOT
 * --------------
 * It is not a vault UI for credentials. F 233's vault seals node CONTENT;
 * `freshcards/credentials` is a different surface with a different unlock API
 * (F 238's barrel note says why they must not be merged).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Text, TextInput, UnstyledButton, ActionIcon, Group, Badge, Tooltip } from '@mantine/core';
import {
  IconLock, IconLockOpen, IconTag, IconSparkles, IconCheck, IconX, IconPlus,
} from '@tabler/icons-react';
// F 250 — these two modules, not '../../../core'.
//
// F 237 stopped because the seven F 233 helpers were unreachable from the
// public barrel; F 238 re-exported them and F 243 proved the gate was clear
// by importing the barrel HERE. That was the right proof and the wrong
// production import: the barrel reaches refs/usedIn.ts (F 227), which
// reaches adapters/filesystem/references.ts, which imports node:fs/promises.
// vite.config.ts externalises no node builtin, so the app build died on
//   references.ts (19:9): "readFile" is not exported by
//   "__vite-browser-external".
//
// The barrel is unchanged and still exports every one of these — F 238's
// surface stays intact for library consumers. A browser entry just should
// not pull a filesystem adapter in to read a passphrase. These two modules
// reach ../../crypto and one type, and nothing else.
//
// F 243's R1 test asserted the import came from the BARREL. That assertion is
// what produced the break, so it is corrected in the same commit: the gate
// lives in src/core.ts, so that is where reachability is proven.
import {
  encryptNode,
  decryptNode,
  unlockVault,
  lockVault,
  isVaultUnlocked,
  setNodeStore,
  type EncryptedNode,
  type NodeStore,
} from '../../../core/credentials/vault';
import { suggestAnchors, setAnchors, type Anchor } from '../../../core/anchors';
import './encryption-panel.css';

// ─── the store the vault reads and writes ──────────────────────────────────

/**
 * The node store, in memory, held at module scope.
 *
 * F 233 deliberately made the store an injected dependency rather than a
 * module global of its own, and nothing in production has called
 * `setNodeStore` yet — so this UI is the first real caller, and it has to
 * supply one. An in-memory store is honest about what it is: the F 233
 * filesystem backend is for the CREDENTIAL store, not for entity content, and
 * silently reusing it would put node bodies in `~/.freshcards` with no
 * migration story.
 *
 * Replacing this with a real persistence adapter is a one-line change and is
 * deliberately left as the caller's decision, not guessed at here.
 */
/**
 * `EncryptedNode` declares `anchors?: unknown[]`; `anchors.ts`'s own
 * `AnchorStore` wants `anchors?: Anchor[]`. The intersection satisfies both
 * without casting at every call site.
 *
 * FINDING, reported not worked around silently: `AnchorStore` is NOT exported
 * from `freshcards/core`. The seven HELPERS are all reachable — that gate is
 * clear — but a consumer calling `setAnchors`/`suggestAnchors` cannot name the
 * parameter type it must satisfy. Same class of gap as F 237's gate, one level
 * down. The workaround is one intersection; the fix is one `export type` line.
 */
type UiNode = EncryptedNode & { anchors?: Anchor[] };

const nodes = new Map<string, UiNode>();

export const memoryNodeStore = {
  async get(id: string) { return nodes.get(id); },
  async set(id: string, node: UiNode) { nodes.set(id, node); },
} satisfies NodeStore;

let storeInstalled = false;
function ensureStore(): void {
  if (storeInstalled) return;
  setNodeStore(memoryNodeStore);
  storeInstalled = true;
}

/** Test seam: the in-memory map, so a test can seed a node without a UI. */
export function __seedNode(node: UiNode): void { nodes.set(node.id, node); }

// ─── prompts ──────────────────────────────────────────────────────────────

/**
 * F 233's `unlockVault` takes a VERIFIER: it does not store a passphrase, it
 * asks something that knows whether this passphrase is right. There is no
 * credential record to verify against in this UI, so the verifier is a
 * passphrase EQUALITY check against a value the caller supplies. That is
 * honest — it is a local passphrase prompt, not a credential unlock — and it
 * is why the prompt takes a `passphrase` prop rather than discovering one.
 */
export interface PassphrasePromptProps {
  opened: boolean;
  onCancel: () => void;
  onUnlock: (passphrase: string) => void;
  /** The passphrase to verify against. Compared, never stored by the vault. */
  expected: string;
  error?: string | null;
}

export const PassphrasePrompt: React.FC<PassphrasePromptProps> = ({
  opened, onCancel, onUnlock, expected, error,
}) => {
  const [value, setValue] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => { if (opened) { setValue(''); setLocalError(null); } }, [opened]);
  if (!opened) return null;

  const submit = () => {
    // F 233 separates "wrong passphrase" from "vault locked" in its read
    // path. The prompt must do the same: one says the key was tried and
    // rejected, the other says there was no key to try.
    if (value !== expected) { setLocalError('Wrong passphrase'); return; }
    onUnlock(value);
  };

  const shown = localError ?? error ?? null;

  return (
    <Box className="fc-enc__scrim" data-testid="fc-enc-prompt-scrim">
      <Box className="fc-enc__prompt" data-testid="fc-enc-prompt" role="dialog" aria-modal="true"
        aria-label="Unlock vault">
        <Group gap="xs" mb="xs">
          <IconLock size={15} aria-hidden="true" />
          <Text size="sm" fw={500}>Unlock vault</Text>
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          Encrypted content stays sealed until the vault is unlocked.
        </Text>
        <TextInput
          data-testid="fc-enc-passphrase"
          type="password"
          value={value}
          placeholder="Passphrase"
          onChange={(e) => { setValue(e.currentTarget.value); setLocalError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          aria-invalid={shown ? true : undefined}
        />
        {shown && (
          <Text size="xs" c="red" mt={6} data-testid="fc-enc-prompt-error" role="alert">{shown}</Text>
        )}
        <Group justify="flex-end" gap="xs" mt="sm">
          <UnstyledButton className="fc-enc__btn" onClick={onCancel} data-testid="fc-enc-cancel">Cancel</UnstyledButton>
          <UnstyledButton className="fc-enc__btn" data-variant="primary" onClick={submit}
            data-testid="fc-enc-unlock-submit">Unlock</UnstyledButton>
        </Group>
      </Box>
    </Box>
  );
};

// ─── anchor editor ────────────────────────────────────────────────────────

export interface AnchorEditorProps {
  entityId: string | null;
  unlocked: boolean;
  onSaved?: (anchors: Anchor[]) => void;
}

/**
 * The plaintext hint surface. Editable only while the vault is open, and
 * editable ANCHORS only — this never shows or edits content, which is the
 * entire point of F 233's design: the AI searches these and cannot read the
 * thing they describe.
 */
export const AnchorEditor: React.FC<AnchorEditorProps> = ({ entityId, unlocked, onSaved }) => {
  const [anchors, setLocal] = useState<Anchor[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [suggested, setSuggested] = useState<Anchor[] | null>(null);

  const refresh = useCallback(async () => {
    if (!entityId) return;
    const n = await memoryNodeStore.get(entityId);
    setLocal(Array.isArray(n?.anchors) ? (n.anchors as Anchor[]) : []);
  }, [entityId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const save = useCallback(async (next: Anchor[]) => {
    if (!entityId) return;
    const r = await setAnchors(entityId, next, memoryNodeStore);
    if (r.ok) { setLocal(next); setSuggested(null); onSaved?.(next); }
  }, [entityId, onSaved]);

  const suggest = useCallback(async () => {
    if (!entityId) return;
    const r = await suggestAnchors(entityId, memoryNodeStore);
    // F 233's suggestAnchors persists NOTHING and refuses on a locked node.
    // An empty array is a real answer, not a missing model, so the button
    // stays put and simply adds nothing.
    if (r.ok) setSuggested(r.value ?? []);
  }, [entityId]);

  if (!entityId) return null;

  const accept = (a: Anchor) => {
    // Accepting an AI suggestion promotes its provenance to 'user'. The user
    // has now stood behind it, and the record should say so.
    void save([...anchors, { ...a, id: `${entityId}:${anchors.length}`, source: 'user' }]);
  };
  const addBlank = () => {
    if (!draft.trim()) return;
    void save([...anchors, {
      id: `${entityId}:${anchors.length}`, text: draft.trim(), kind: 'hint',
      source: 'user', createdAt: new Date().toISOString(),
    }]);
    setDraft('');
  };

  return (
    <Box data-testid="fc-enc-anchors" className="fc-enc__section">
      <Group gap="xs" mb={6}>
        <IconTag size={14} aria-hidden="true" />
        <Text size="sm" fw={500}>Search anchors</Text>
        <Text size="xs" c="dimmed" className="fc-detail__count">{anchors.length}</Text>
        {unlocked && (
          <>
            <Tooltip label="Suggest anchors from the content">
              <UnstyledButton className="fc-enc__btn fc-enc__btn--sm" onClick={() => void suggest()}
                data-testid="fc-enc-suggest">
                <IconSparkles size={12} aria-hidden="true" /> Suggest
              </UnstyledButton>
            </Tooltip>
            <UnstyledButton className="fc-enc__btn fc-enc__btn--sm" onClick={() => setEditingId('__blank__')}
              data-testid="fc-enc-add">
              <IconPlus size={12} aria-hidden="true" /> Add
            </UnstyledButton>
          </>
        )}
      </Group>

      {!unlocked && anchors.length === 0 && (
        <Text size="xs" c="dimmed" data-testid="fc-enc-anchors-locked-empty">
          No anchors. Unlock the vault to add or edit them.
        </Text>
      )}

      {anchors.length > 0 && (
        <Group gap={6} data-testid="fc-enc-anchor-chips">
          {anchors.map((a) => (
            <Badge key={a.id} variant="light" data-testid="fc-enc-chip" data-source={a.source}
              leftSection={a.source === 'ai'
                ? <IconSparkles size={10} aria-label="AI suggested" />
                : <IconCheck size={10} aria-label="user authored" />}>
              {editingId === a.id ? (
                <TextInput size="xs" value={draft} onChange={(e) => setDraft(e.currentTarget.value)}
                  data-testid="fc-enc-anchor-draft" autoFocus />
              ) : a.text}
            </Badge>
          ))}
        </Group>
      )}

      {/* Locked vaults show the chips read-only: an anchor is plaintext, so
          showing it is safe, and hiding it would make an encrypted node
          unsearchable even for its owner. */}
      {unlocked && editingId === '__blank__' && (
        <Group gap="xs" mt={6}>
          <TextInput size="xs" value={draft} placeholder="A hint the AI can search"
            onChange={(e) => setDraft(e.currentTarget.value)} data-testid="fc-enc-new-anchor" />
          <UnstyledButton className="fc-enc__btn fc-enc__btn--sm" onClick={addBlank}
            data-testid="fc-enc-new-anchor-save">Save</UnstyledButton>
        </Group>
      )}

      {unlocked && suggested && suggested.length > 0 && (
        <Box mt="xs" data-testid="fc-enc-suggestions">
          <Text size="xs" c="dimmed" mb={4}>Suggested from the content — accept or reject.</Text>
          <Group gap={6}>
            {suggested.map((s) => (
              <Group key={s.id} gap={2} className="fc-enc__suggestion" data-testid="fc-enc-suggestion">
                <Badge variant="outline" size="sm" data-testid="fc-enc-suggestion-chip">{s.text}</Badge>
                <ActionIcon size="xs" variant="subtle" onClick={() => accept(s)}
                  data-testid="fc-enc-accept" aria-label={`Accept ${s.text}`}>
                  <IconCheck size={11} />
                </ActionIcon>
                <ActionIcon size="xs" variant="subtle" onClick={() => setSuggested(null)}
                  data-testid="fc-enc-reject" aria-label={`Reject ${s.text}`}>
                  <IconX size={11} />
                </ActionIcon>
              </Group>
            ))}
          </Group>
        </Box>
      )}

      {unlocked && (
        <UnstyledButton className="fc-enc__btn fc-enc__btn--sm" mt={6}
          onClick={() => void save(anchors.filter((a) => a.id !== (editingId ?? '__none__')))}
          data-testid="fc-enc-remove-editing" hidden={editingId === null || editingId === '__blank__'}>
          <IconX size={12} aria-hidden="true" /> Remove
        </UnstyledButton>
      )}
    </Box>
  );
};

// ─── the panel ────────────────────────────────────────────────────────────

export interface EncryptionPanelProps {
  entityId: string | null;
  /** The passphrase the local vault is verified against. */
  passphrase: string;
  onVaultStateChange?: (unlocked: boolean) => void;
}

/**
 * The whole surface in one panel: toggle, status, anchors, prompt.
 *
 * The toggle is deliberately NOT optimistic. `encryptNode` can fail — no
 * store, already encrypted, no content — and a switch that shows "on" while
 * the node is still plaintext is worse than no switch at all.
 */
export const EncryptionPanel: React.FC<EncryptionPanelProps> = ({
  entityId, passphrase, onVaultStateChange,
}) => {
  const [unlocked, setUnlocked] = useState(false);
  const [node, setNode] = useState<EncryptedNode | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promptFor, setPromptFor] = useState<'encrypt' | 'decrypt' | null>(null);

  useEffect(() => { ensureStore(); }, []);

  const refresh = useCallback(async () => {
    if (!entityId) { setNode(undefined); return; }
    setNode(await memoryNodeStore.get(entityId));
  }, [entityId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const encrypted = node?.encrypted === true;
  const busyGuard = useMemo(() => busy || !entityId, [busy, entityId]);

  const run = useCallback(async (dir: 'encrypt' | 'decrypt') => {
    if (!entityId) return;
    setBusy(true); setError(null);
    try {
      // NOT a ternary: `cond ? await f() : await g()` produces a union of two
      // Result unions and TypeScript stops distributing the `ok` discriminant,
      // so `r.error` does not narrow. Two statements narrow correctly.
      const r = dir === 'encrypt'
        ? await encryptNode(entityId, passphrase, memoryNodeStore)
        : await decryptNode(entityId, passphrase, memoryNodeStore);
      const okFlag = r.ok;
      if (!okFlag) { setError((r as { error: string }).error); return; }
      // F 285: the manual write-back that used to live here is GONE.
      // `decryptNode` persists the plaintext itself now, so the panel and the
      // store cannot disagree — this was the last caller compensating for the
      // function, and one more caller would have forgotten to.
      await refresh();
    } finally { setBusy(false); }
  }, [entityId, passphrase, refresh]);

  const toggle = useCallback(async () => {
    if (!unlocked) { setPromptFor(encrypted ? 'decrypt' : 'encrypt'); return; }
    await run(encrypted ? 'decrypt' : 'encrypt');
  }, [unlocked, encrypted, run]);

  const onUnlock = useCallback(async () => {
    const r = await unlockVault(passphrase, (p) => p === passphrase);
    if (!r.ok) { setError((r as { error: string }).error); return; }
    setUnlocked(true);
    onVaultStateChange?.(true);
    const pending = promptFor;
    setPromptFor(null);
    if (pending) await run(pending);
  }, [passphrase, promptFor, run, onVaultStateChange]);

  const onCancel = useCallback(() => {
    setPromptFor(null);
    // Cancelling must NOT leave the vault half-open, and must not lock a
    // vault the user had already opened: only the vault this prompt opened.
    if (isVaultUnlocked()) { lockVault(); setUnlocked(false); onVaultStateChange?.(false); }
  }, [onVaultStateChange]);

  if (!entityId) return null;

  return (
    <Box data-testid="fc-enc-panel" className="fc-enc">
      <Group gap="xs" className="fc-enc__row">
        <UnstyledButton
          className="fc-enc__btn"
          data-testid="fc-enc-toggle"
          data-encrypted={encrypted ? 'true' : 'false'}
          disabled={busyGuard}
          onClick={() => void toggle()}
          aria-pressed={encrypted}
        >
          {encrypted
            ? <IconLock size={14} aria-hidden="true" />
            : <IconLockOpen size={14} aria-hidden="true" />}
          <Text size="xs">{encrypted ? 'Encrypted' : 'Encrypt this'}</Text>
        </UnstyledButton>

        <Tooltip label={unlocked ? 'Vault unlocked — click to lock' : 'Vault locked — click to unlock'}>
          <UnstyledButton
            className="fc-enc__btn fc-enc__btn--sm"
            data-testid="fc-enc-vault-indicator"
            data-unlocked={unlocked ? 'true' : 'false'}
            onClick={() => {
              if (isVaultUnlocked()) { lockVault(); setUnlocked(false); onVaultStateChange?.(false); }
              else setPromptFor('encrypt');
            }}
          >
            {unlocked ? <IconLockOpen size={13} aria-hidden="true" />
                      : <IconLock size={13} aria-hidden="true" />}
            <Text size="xs">{unlocked ? 'Vault unlocked' : 'Vault locked'}</Text>
          </UnstyledButton>
        </Tooltip>
      </Group>

      {error && (
        <Text size="xs" c="red" mt={4} data-testid="fc-enc-error" role="alert">{error}</Text>
      )}

      <AnchorEditor entityId={entityId} unlocked={unlocked} />
      {unlocked && (
        <UnstyledButton className="fc-enc__btn fc-enc__btn--sm" mt={4}
          data-testid="fc-enc-lock-now"
          onClick={() => { lockVault(); setUnlocked(false); onVaultStateChange?.(false); }}>
          <IconLock size={12} aria-hidden="true" /> Lock now
        </UnstyledButton>
      )}

      <PassphrasePrompt
        opened={promptFor !== null}
        expected={passphrase}
        error={error}
        onCancel={onCancel}
        onUnlock={() => void onUnlock()}
      />
    </Box>
  );
};

export default EncryptionPanel;
